import { lazy, Suspense, useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useStatus } from '@powersync/react';
import { format, isToday } from 'date-fns';
import {
  CableIcon,
  CircleUserIcon,
  CloudIcon,
  CloudOffIcon,
  PlaneIcon,
  RefreshCcwIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { SignInForm } from '~/features/auth/components/authForm';
import { useFetchAuthStatus } from '~/features/auth/hooks/useFetchAuthStatus';
import type { PostSignInResponse } from '~/features/auth/hooks/usePostSignIn';
import { useDebounceValue } from '~/hooks/useDebounceValue';
import { useTimeout } from '~/hooks/useTimeout';
import { useIsOnline } from '~/states/isOnlineContext';
import { useUserStore } from '~/states/stores/userStore';
import { isDemo } from '~/utils/demo';
import { APIError } from '~/utils/errors';
import { LocalDataActions } from './LocalDataActions';
import SignOutButton from './SignOutButton';
import UpdatePrompt from './UpdatePrompt';
import { Button, buttonVariants } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const LazyDemoIndicator = lazy(() => import('./DemoIndicator'));

interface NotSignedInContentProps {
  isSyncEnabledUser: boolean;
  onSignInCallback: (userData: PostSignInResponse['user']) => void;
}
const NotSignedInContent = ({
  isSyncEnabledUser,
  onSignInCallback,
}: NotSignedInContentProps) => {
  return (
    <>
      <div className="space-y-2 pb-4">
        <h4 className="leading-none font-medium">
          <Trans>Sync is off</Trans>
        </h4>
        {isSyncEnabledUser ? (
          <div className="text-muted-foreground text-sm">
            <p>
              <Trans>Your session has expired.</Trans>
            </p>
            <p>
              <Trans>Sign in again to sync your data.</Trans>
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            <Trans>Sign in to sync your data.</Trans>
          </p>
        )}
      </div>

      <SignInForm onSignInCallback={onSignInCallback} />
    </>
  );
};

const SyncErrorContent = () => {
  return (
    <div className="space-y-2">
      <h4 className="leading-none font-medium">
        <Trans>Sync is off</Trans>
      </h4>
      <p className="text-muted-foreground text-sm text-pretty">
        <Trans>Could not connect with the sync service. Try again later.</Trans>
      </p>
    </div>
  );
};

type SyncStatusDisplay =
  | 'offline'
  | 'connecting'
  | 'synced'
  | 'notSignedIn'
  | 'error';

function StatusMenu() {
  const { t } = useLingui();
  const {
    connected: isSyncConnected,
    dataFlowStatus: { downloading, uploading },
    lastSyncedAt,
    hasSynced,
    connecting,
  } = useStatus();

  const [userName, useSync] = useUserStore(
    useShallow((state) => [state.user.name, state.user.useSync]),
  );
  const [isFirstConnecting, setIsFirstConnecting] = useState(true);

  const { data, isLoading, isError, error } = useFetchAuthStatus();

  const isSignedIn = !!data?.result;
  const isOnline = useIsOnline();

  const isSyncing = downloading || uploading;
  const debouncedIsSyncing = useDebounceValue(isSyncing, 200);
  const debouncedConnecting = useDebounceValue(connecting, 400);

  const [open, setOpen] = useState(false);

  // NOTE: timeout for when sync server is not responding
  const hasTimedOut = useTimeout({
    delay: 60000, // 60 seconds
    enabled: !isSyncConnected && isSignedIn && isOnline,
  });

  const display: SyncStatusDisplay = (() => {
    if (!isOnline) return 'offline';
    if (isError && (!(error instanceof APIError) || error.status !== 401))
      return 'error';
    if (!isSignedIn) return 'notSignedIn';
    if (hasTimedOut) return 'error';
    if (isFirstConnecting || debouncedConnecting) return 'connecting';
    if (isSyncConnected && (isSyncing || hasSynced)) return 'synced';
    return 'error';
  })();

  // NOTE: fix for delay in the connecting state to become true when the component is mounted
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsFirstConnecting(false);
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, []);

  if (isLoading) {
    return (
      <output
        className="ml-auto flex h-9 w-[96px] items-center justify-center gap-2 rounded-xl bg-gray-200/50 px-2 py-1"
        aria-label={t`Loading user status`}
      >
        <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500 [animation-delay:-0.3s]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500 [animation-delay:-0.15s]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-gray-500" />
      </output>
    );
  }

  if (isDemo) {
    return (
      <div className="mb-1 ml-auto flex min-h-[40px] items-center gap-2">
        <UpdatePrompt />
        <Suspense fallback={null}>
          <LazyDemoIndicator />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="ml-auto flex items-center gap-2 rounded-xl bg-gray-200/50 px-4 py-1">
      {debouncedIsSyncing && (
        <output
          aria-live="polite"
          aria-atomic="true"
          className={buttonVariants({
            variant: 'ghost',
            size: 'icon-responsive',
          })}
        >
          <span className="sr-only">{t`Syncing`}</span>
          <RefreshCcwIcon
            size={18}
            className="direction-reverse animate-spin"
          />
        </output>
      )}
      <UpdatePrompt />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              size="icon-responsive"
              variant="ghost"
              aria-label={t`Check sync status`}
            />
          }
        >
          {display === 'offline' ? (
            <PlaneIcon size={18} />
          ) : display === 'connecting' ? (
            <CableIcon size={18} />
          ) : display === 'synced' ? (
            <CloudIcon size={18} />
          ) : (
            <CloudOffIcon size={18} />
          )}
        </PopoverTrigger>
        <PopoverContent
          className={
            display === 'synced'
              ? 'mt-0.5 mr-3 max-w-fit min-w-[121px] sm:mr-8'
              : 'mt-0.5 mr-3 sm:mr-8'
          }
          sideOffset={5}
        >
          {display === 'synced' && (
            <div className="space-y-2">
              <h3 className="leading-none font-medium text-pretty">
                <Trans>Your data is synced!</Trans>
              </h3>
              {lastSyncedAt && (
                <div className="text-muted-foreground">
                  <p className="text-sm">
                    <Trans>Last synced:</Trans>{' '}
                  </p>
                  <p className="text-xs">{format(lastSyncedAt, 'p')} </p>
                  {!isToday(lastSyncedAt) && (
                    <p className="text-xs">{format(lastSyncedAt, 'P')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {display === 'offline' && (
            <div className="space-y-2">
              <h4 className="leading-none font-medium">
                <Trans>You're offline</Trans>
              </h4>
              <p className="text-muted-foreground text-sm">
                <Trans>Check your internet connection and try again.</Trans>
              </p>
            </div>
          )}

          {display === 'connecting' && (
            <div className="space-y-2">
              <h4 className="leading-none font-medium">
                <Trans>Connecting to sync…</Trans>
              </h4>
              <p className="text-muted-foreground text-pretty break-keep">
                <Trans>
                  Establishing a connection. This may take a moment.
                </Trans>
              </p>
            </div>
          )}

          {display === 'notSignedIn' && (
            <NotSignedInContent
              isSyncEnabledUser={Boolean(useSync)}
              onSignInCallback={({ name: userName }) => {
                setOpen(false);
                userName && toast.success(t`Welcome back, ${userName}!`);
              }}
            />
          )}

          {display === 'error' && <SyncErrorContent />}
        </PopoverContent>
      </Popover>
      <Popover>
        <PopoverTrigger
          render={
            <Button
              size="icon-responsive"
              variant="ghost"
              aria-label={t`User info`}
            />
          }
        >
          <CircleUserIcon size={18} />
        </PopoverTrigger>
        <PopoverContent className="mr-3 w-min max-w-96 sm:mr-8" sideOffset={5}>
          <div className="space-y-2 pb-4">
            <h3 className="text-center leading-none font-medium text-pretty">
              <Trans>Hello, {userName}!</Trans>
            </h3>
          </div>
          <div className="flex flex-col gap-2">
            <LocalDataActions />
            {(isSignedIn || Boolean(useSync)) && <SignOutButton />}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default StatusMenu;
